import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, catchError, debounceTime, distinctUntilChanged, filter, map, merge, of, switchMap, takeWhile } from 'rxjs';
import { HelperFunctions } from 'src/app/util/util-functions';
import { Paginator } from '../../models/paginator';
import { PageableService } from '../../services/pageable.service';
import { FilterOption } from '../typeahead/typeahead.component';

@Component({
    selector: 'app-dynamic-typeahead',
    templateUrl: './dynamic-typeahead.component.html',
    standalone: false
})
export class DynamicTypeaheadComponent<T> implements OnDestroy {
  @Input() resetInput = false;
  @Input() editable = true;
  @Input() control: FormControl;
  @Input() method: string;
  @Input() maxQuantity: number;
  @Input() prop: string;
  @Input() classes = "typeahead-relative";
  // skipcq: JS-0323
  @Input() filterOptions: FilterOption<any>;
  @Output() readonly selectEvent = new EventEmitter<T>();
  @ViewChild('instance', { static: true }) instance: NgbTypeahead
  private alive = true;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();
  // skipcq: JS-0323
  formatter = (result: any): string => result[this.prop];

  constructor(private readonly pageableService: PageableService) { };

  /**
* A function that search the value in the filtered option list
* @returns { OperatorFunction<string, T[]>}
*/
  search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(500), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      takeWhile(() => this.alive),
      switchMap((term) => this.pageableService.getData(this.method, 0, this.maxQuantity, this.prop, term).pipe(
        catchError(() => {
          const paginator: Paginator<T> = {
            content: [],
            numberOfElements: 0,
            totalPages: 1,
            totalElements: 0,
            number: 0
          };
          return of(paginator)
        })
        , map(results => {
          let currentResults = results.content;
          if (this.filterOptions) {
            // skipcq: JS-0323
            currentResults = results.content.filter((options: any) => options[this.filterOptions.prop] === this.filterOptions.value)
          }
          return currentResults
        })))
    );
  };

  onSelect(event: NgbTypeaheadSelectItemEvent) {
    const item = event.item;
    this.selectEvent.emit(item);
    if (this.resetInput)
      event.preventDefault();
  }

  /**
  * A function that checks if the control is invalid
  * @returns {boolean}
  */
  hasErrors(): boolean {
    return HelperFunctions.controlIsInvalid(this.control);
  }

  ngOnDestroy(): void {
    this.alive = false;
  }
}
